import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product } from '@/mocks/products';

interface RelatedProductsProps {
  products: Product[];
  currentSlug: string;
}

const RelatedProducts = ({ products, currentSlug }: RelatedProductsProps) => {
  const { t } = useTranslation();

  const related = products
    .filter(p => p.slug !== currentSlug)
    .slice(0, 4);

  if (related.length === 0) return null;

  return (
    <section className="border-t border-white/8 pt-16 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[#c9a84c] text-[10px] tracking-[0.35em] uppercase mb-2 font-semibold">
              {t('product_related_label', 'Você Também Pode Gostar')}
            </p>
            <h2
              className="text-3xl font-light text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t('product_related_title', 'Produtos Relacionados')}
            </h2>
          </div>
          <Link
            to="/loja"
            className="hidden sm:flex items-center gap-2 text-white/40 hover:text-[#c9a84c] text-xs tracking-widest uppercase transition-colors whitespace-nowrap"
          >
            {t('product_see_all', 'Ver todos')}
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {related.map(product => (
            <Link
              key={product.id}
              to={`/produto/${product.slug}`}
              className="group bg-[#111111] border border-white/8 hover:border-[#c9a84c]/25 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="relative bg-gradient-to-b from-[#161616] to-[#0d0d0d] flex items-center justify-center p-6 h-52">
                {product.badge && (
                  <div className="absolute top-3 left-3 bg-[#c9a84c] text-black text-[9px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {product.badge}
                  </div>
                )}
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-36 w-auto object-contain object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Info */}
              <div className="p-5">
                <p className="text-[#c9a84c] text-[9px] tracking-[0.3em] uppercase mb-1 font-medium">
                  {product.category === 'whisky' ? 'Whisky' : 'Gin'}
                  {product.region ? ` · ${product.region}` : ''}
                </p>
                <h3 className="text-white text-base font-light mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {product.name}
                </h3>
                <p className="text-white/35 text-xs mb-4 line-clamp-2 font-light">{product.description}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[#c9a84c] text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {product.priceFormatted}
                  </p>
                  <div className="w-8 h-8 flex items-center justify-center border border-white/15 group-hover:border-[#c9a84c]/40 group-hover:text-[#c9a84c] rounded-full text-white/40 transition-all">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedProducts;
